import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortfolioHealthCheckApi } from './portfolioHealthCheckApi';
import { usePortfolioHealthCheck } from './usePortfolioHealthCheck';
import PortfolioSummary from './PortfolioSummary';
import HoldingsReviewList from './HoldingsReviewList';
import PortfolioAnalysis from './PortfolioAnalysis';
import HealthCheckReport from './HealthCheckReport';
import socket from '../../../../context/socket';
import './portfolio-health-check.css';

/**
 * Root content component for the GoalTracker Portfolio Health Check with full cobrowsing sync.
 *
 * Before fetching:
 * - Image 1 intro with "Explore a real portfolio analysis ↓"
 * - Image 2: "Review your external investments" empty state
 * - Image 3: "How to use" & "What the figures mean" / definitions
 * - Image 4: Sample Portfolio Health Check report
 *
 * After fetching:
 * - Image 1 intro with button changed to "Explore your portfolio analysis ↓"
 * - Images 2, 3, and 4 are removed.
 * - Live portfolio result (Image 5 style): PortfolioSummary + HoldingsReviewList + Analyse Actionbar
 * - After analysis: PortfolioAnalysis with shared companies card and detailed comparison accordions.
 * - After health check: HealthCheckReport with diagnosis and structural breakdown.
 */
export default function PortfolioReview({
  clientId,
  pan,
  api,
  apiConfig,
  ready,
  fetching = false,
  onRequestFetch,
  reviewLinks,
  onClose,
  roomId,
  isClient = false,
}) {
  const activeRoom = roomId || clientId;

  const client = useMemo(
    () => api || createPortfolioHealthCheckApi(apiConfig),
    [api, apiConfig]
  );
  const s = usePortfolioHealthCheck(client, clientId, pan);

  // Synchronized state for Client side
  const [syncReview, setSyncReview] = useState(null);
  const [syncAnalysis, setSyncAnalysis] = useState(null);
  const [syncHealth, setSyncHealth] = useState(null);
  const [syncBusy, setSyncBusy] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [uiState, setUiState] = useState({
    openPair: null,
    expandedCompanies: {},
    showAllShared: false,
    openAccordions: { attention: true, notCompared: true, matched: true, scope: false, pairs: false, companySector: false, singleFund: false, coverage: false },
    changing: null,
    editingAmounts: {},
  });

  // Effective data depending on role
  const effectiveReview = isClient ? (syncReview || (ready ? s.review : null)) : s.review;
  const effectiveAnalysis = isClient ? (syncAnalysis || s.analysis) : s.analysis;
  const effectiveHealth = isClient ? (syncHealth || s.health) : s.health;
  const effectiveBusy = isClient ? (syncBusy !== null ? syncBusy : s.busy) : s.busy;
  const effectiveError = isClient ? (syncError !== null ? syncError : s.error) : s.error;

  // Broadcast helper for Advisor
  const broadcastSync = useCallback((override = {}) => {
    if (isClient || !socket || !activeRoom) return;
    socket.emit("join", { room: activeRoom });
    socket.emit("message_to", {
      room: activeRoom,
      data: {
        location: "phc_cobrowse_sync",
        payload: {
          review: s.review,
          analysis: s.analysis,
          health: s.health,
          busy: s.busy,
          error: s.error,
          uiState,
          ...override,
        },
      },
    });
  }, [isClient, activeRoom, s.review, s.analysis, s.health, s.busy, s.error, uiState]);

  // Advisor: Load review when ready
  useEffect(() => {
    if (!isClient && ready && clientId) {
      s.loadReview(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, ready, clientId, pan]);

  // Advisor: Broadcast state whenever review, analysis, health, busy, or error changes
  useEffect(() => {
    if (!isClient) {
      broadcastSync();
    }
  }, [isClient, broadcastSync, s.review, s.analysis, s.health, s.busy, s.error]);

  // Advisor: Respond to Client request for state
  useEffect(() => {
    if (isClient || !socket || !activeRoom) return;

    socket.emit("join", { room: activeRoom });
    const handleSyncRequest = (msg) => {
      if (msg?.data?.location === "phc_cobrowse_request") {
        broadcastSync();
      }
    };

    socket.on("message_to", handleSyncRequest);
    return () => socket.off("message_to", handleSyncRequest);
  }, [isClient, activeRoom, broadcastSync]);

  // Client: Listen for synced state from Advisor
  useEffect(() => {
    if (!isClient || !socket || !activeRoom) return;

    socket.emit("join", { room: activeRoom });
    // Request current state from Advisor
    socket.emit("message_to", {
      room: activeRoom,
      data: {
        location: "phc_cobrowse_request",
      },
    });

    const handleSync = (msg) => {
      if (msg?.data?.location === "phc_cobrowse_sync" && msg.data.payload) {
        const p = msg.data.payload;
        if (p.review !== undefined) setSyncReview(p.review);
        if (p.analysis !== undefined) setSyncAnalysis(p.analysis);
        if (p.health !== undefined) setSyncHealth(p.health);
        if (p.busy !== undefined) setSyncBusy(p.busy);
        if (p.error !== undefined) setSyncError(p.error);
        if (p.uiState !== undefined) {
          setUiState((prev) => ({
            ...prev,
            ...p.uiState,
            openAccordions: { ...prev.openAccordions, ...(p.uiState.openAccordions || {}) },
            expandedCompanies: { ...prev.expandedCompanies, ...(p.uiState.expandedCompanies || {}) },
            editingAmounts: { ...prev.editingAmounts, ...(p.uiState.editingAmounts || {}) },
          }));
        }
      }
    };

    socket.on("message_to", handleSync);
    return () => socket.off("message_to", handleSync);
  }, [isClient, activeRoom]);

  // Handle UI changes (dropdown selection, accordion toggles, amounts typing)
  const handleUiChange = useCallback((patch) => {
    setUiState((prev) => {
      const next = {
        ...prev,
        ...patch,
        openAccordions: patch.openAccordions ? { ...prev.openAccordions, ...patch.openAccordions } : prev.openAccordions,
        expandedCompanies: patch.expandedCompanies ? { ...prev.expandedCompanies, ...patch.expandedCompanies } : prev.expandedCompanies,
        editingAmounts: patch.editingAmounts ? { ...prev.editingAmounts, ...patch.editingAmounts } : prev.editingAmounts,
      };
      if (!isClient) {
        broadcastSync({ uiState: next });
      }
      return next;
    });
  }, [isClient, broadcastSync]);

  const r = ready ? effectiveReview : null;
  const stale =
    effectiveError &&
    (effectiveError.code === 'review_stale' || effectiveError.code === 'review_expired');

  const scrollToReview = () => {
    document.getElementById('mfr-analysis')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="phc mfr-page">
      {/* Introduction and sample report banner (Image 1) */}
      <section className="mfr-intro">
        <div className="mfr-intro-copy">
          <h1>Mutual Fund Portfolio Review</h1>
          <div className="mfr-intro-text">
            <h2>With the FinEdge Portfolio Health Check</h2>
            <p>
              Go beyond the fund-to-fund overlap to read your portfolio's
              full structure — whether you hold more funds than the design
              needs, whether small positions are fragmenting it, and where
              company exposure concentrates.
            </p>
            <p>
              Upload a CAS or portfolio statement for a ranked diagnosis
              of what deserves attention, with the overlap, allocation and
              company evidence behind it. Or select two or more funds for
              a quick comparison.
            </p>
            <button
              type="button"
              className="mfr-text-link"
              onClick={scrollToReview}
            >
              {r ? 'Explore your portfolio analysis ↓' : 'Explore a real portfolio analysis ↓'}
            </button>
          </div>
        </div>

        <aside className="mfr-mini-report">
          <div className="mfr-mini-report-head">
            <span>REAL EXAMPLE · ONE INVESTOR</span>
            <span className="mfr-outline-pill">Not your result</span>
          </div>
          <h3>More funds. Not necessarily more diversification.</h3>
          <p>
            See how the size of each holding and the companies behind it
            help explain the portfolio's structure.
          </p>
          <div className="mfr-mini-stats">
            <div><strong>14</strong><span>Equity &amp; hybrid funds</span></div>
            <div><strong>85.6%</strong><span>Of their value in just 5</span></div>
          </div>
          <div className="mfr-mini-track"><span /></div>
          <div className="mfr-mini-track-labels">
            <span>Largest 5 funds</span><span>Other 9</span>
          </div>
          <strong className="mfr-mini-subtitle">Several funds too small to shape results</strong>
          <p className="mfr-mini-caption">
            One illustrative example — not a diagnosis of your portfolio.
          </p>
        </aside>
      </section>

      <div className="mfr-divider" />

      {/* Live review / current workflow */}
      <section className="mfr-analysis" id="mfr-analysis">
        {/* Image 2: Shown ONLY when portfolio is NOT fetched yet */}
        {!r && (
          <div className="mfr-analysis-heading">
            <div>
              <p className="mfr-eyebrow">YOUR PORTFOLIO</p>
              <h2>Review your external investments</h2>
              <p>
                Your portfolio inventory, analysis and health findings appear
                here once the external holdings have been fetched.
              </p>
            </div>
          </div>
        )}

        {fetching && (
          <Status
            title="Fetching the external portfolio…"
            text="This uses your existing MF Central verification. The review opens automatically when the holdings are stored."
            spinner
          />
        )}

        {!fetching && !ready && !r && (
          <div className="phc-card phc-fetch-cta">
            <div className="phc-fetch-cta-content">

              <div>
                <h3>No portfolio fetched yet</h3>
                <p>
                  Fetch your client's mutual fund holdings from MFCentral or
                  upload a CAS statement to generate a complete portfolio review.
                </p>
              </div>
            </div>
            {!isClient && onRequestFetch && (
              <button
                type="button"
                className="phc-btn mfr-action-button"
                onClick={onRequestFetch}
              >
                Fetch External Portfolio
              </button>
            )}
            {isClient && (
              <p className="phc-muted" style={{ margin: 0, fontStyle: 'italic' }}>
                Waiting for advisor to fetch portfolio holdings…
              </p>
            )}
          </div>
        )}

        {!fetching && effectiveBusy === 'review' && !r && (
          <Status
            title="Building review…"
            text="We are organizing the fetched holdings for review."
            spinner
          />
        )}

        {!fetching && effectiveError && effectiveError.stage === 'review' && !r && (
          <Status
            title="Could not prepare the holdings for review"
            text={effectiveError.message}
            action={
              effectiveError.code === 'portfolio_not_fetched' && onRequestFetch && !isClient
                ? {
                  label: 'Fetch External Portfolio',
                  onClick: onRequestFetch,
                }
                : !isClient
                  ? {
                    label: 'Try again',
                    onClick: () => s.loadReview(true),
                  }
                  : undefined
            }
            tone="error"
          />
        )}

        {/* Live Fetched Portfolio (Image 5 style): PortfolioSummary + HoldingsReviewList + Actionbar */}
        {r && (
          <div className="mfr-live-content">
            <PortfolioSummary
              review={r}
              isClient={isClient}
              onReload={!isClient ? () => s.loadReview(true) : undefined}
              onRequestFetch={!isClient ? onRequestFetch : undefined}
            />

            {stale && !isClient && (
              <div className="phc-alert phc-alert--error" role="alert">
                {effectiveError.message}{' '}
                <button
                  type="button"
                  className="phc-link"
                  onClick={() =>
                    s.loadReview(effectiveError.code === 'review_expired')
                  }
                >
                  Reload review
                </button>
              </div>
            )}

            {effectiveError && !stale && effectiveError.stage === 'edit' && (
              <div className="phc-alert phc-alert--error" role="alert">
                {effectiveError.message}
              </div>
            )}

            <HoldingsReviewList
              review={r}
              busy={effectiveBusy === 'edit'}
              isClient={isClient}
              uiState={uiState}
              onUiChange={handleUiChange}
              onEdit={!isClient ? s.edit : undefined}
              onSearch={!isClient ? s.search : undefined}
            />

            <div className="phc-actionbar mfr-live-actionbar">
              <button
                type="button"
                className="phc-btn mfr-action-button mfr-analyse-button"
                disabled={!r.canAnalyse || !!effectiveBusy || isClient}
                style={isClient ? { cursor: 'default', opacity: 0.85 } : undefined}
                onClick={!isClient ? s.analyse : undefined}
              >
                {effectiveBusy === 'analyse'
                  ? 'Analysing…'
                  : `Analyse ${r.eligibleFunds} fund${r.eligibleFunds === 1 ? '' : 's'
                  }`}
              </button>
              <p className="phc-muted">
                {r.totals?.holdings || r.eligibleFunds} investment line{r.totals?.holdings === 1 ? '' : 's'} read · {r.buckets?.excluded_by_type?.count || 0} kept in your inventory but outside stock comparison (debt, gold, arbitrage and similar) · {r.eligibleFunds} fund{r.eligibleFunds === 1 ? '' : 's'} ready to compare.
              </p>
            </div>

            {effectiveError && effectiveError.stage === 'analyse' && !stale && (
              <Status
                title="The analysis could not run"
                text={effectiveError.message}
                action={!isClient ? { label: 'Try again', onClick: s.analyse } : undefined}
                tone="error"
              />
            )}

            {effectiveAnalysis && (
              <PortfolioAnalysis
                analysis={effectiveAnalysis}
                isClient={isClient}
                uiState={uiState}
                onUiChange={handleUiChange}
              />
            )}

            {effectiveAnalysis && (
              <section className="phc-card phc-health-cta mfr-health-cta">
                {!effectiveHealth && (
                  <>
                    <p className="phc-eyebrow mfr-eyebrow">
                      FinEdge Portfolio Health Check
                    </p>
                    <h2 className="phc-h2">
                      How these funds fit together
                    </h2>
                    <p className="phc-muted">
                      A structural reading of the eligible funds: breadth,
                      small positions, concentration and Core structure,
                      ranked by what deserves attention.
                    </p>
                    <button
                      type="button"
                      className="phc-btn mfr-action-button"
                      disabled={!!effectiveBusy || isClient}
                      style={isClient ? { cursor: 'default', opacity: 0.85 } : undefined}
                      onClick={!isClient ? s.runHealth : undefined}
                    >
                      {effectiveBusy === 'health'
                        ? 'Checking…'
                        : effectiveError && effectiveError.stage === 'health'
                          ? 'Try again'
                          : 'Run Portfolio Health Check'}
                    </button>
                    {effectiveError && effectiveError.stage === 'health' && (
                      <p className="phc-error" role="status">
                        {effectiveError.message}
                      </p>
                    )}
                  </>
                )}
                {effectiveHealth && (
                  <HealthCheckReport health={effectiveHealth} links={reviewLinks} />
                )}
              </section>
            )}
          </div>
        )}
      </section>

      {/* Images 3 and 4: Only shown BEFORE fetching (!r) */}
      {!r && (
        <>
          <div className="mfr-divider" />

          {/* Image 3: How to use */}
          <section className="mfr-how">
            <h2>How to use</h2>
            <div className="mfr-three-columns">
              <article>
                <h3>1. Add your investments</h3>
                <p>
                  Open a digital CAS or portfolio statement, or select at least
                  two equity funds or eligible hybrids.
                </p>
              </article>
              <article>
                <h3>2. Check the inventory</h3>
                <p>
                  Identify the schemes in your inventory, including debt and gold.
                  Resolve uncertain identities or values that affect the
                  comparison.
                </p>
              </article>
              <article>
                <h3>3. Read the diagnosis, then the evidence</h3>
                <p>
                  Start with what deserves attention, then review fund pairs,
                  company exposure and allocation evidence.
                </p>
              </article>
            </div>
          </section>

          <div className="mfr-divider" />

          {/* Image 3 (part 2): Definitions */}
          <section className="mfr-definitions">
            <article>
              <h3>What the figures mean</h3>
              <p>
                <strong>Portfolio overlap</strong> is the share of two funds'
                stock portfolios invested in the same companies at the same
                time, weighted by each fund's allocation to that stock.
              </p>
              <p>
                It is computed pairwise from disclosed portfolio dates. Where
                dates differ materially, the overlap is marked indicative.
              </p>
            </article>
            <article>
              <h3>What overlap does not tell you</h3>
              <p>
                Overlap is not a percentage of your whole portfolio, and it is
                not money wasted. Two index funds tracking the same index
                overlap heavily by design; two funds from different categories
                should not.
              </p>
              <p>
                A high overlap simply points to where you may be paying two
                managers for roughly the same underlying bets.
              </p>
            </article>
          </section>

          <div className="mfr-divider" />

          {/* Image 4: Sample Portfolio Health Check Report */}

          {/* Image 4: Sample Portfolio Health Check Report */}
          <section className="mfr-sample-report">

            {/* Report header */}
            <header
              className="mfr-sample-report-header"
              style={{
                display: "block",
                borderRadius: "12px 12px 0 0",
                width: "100%",
                minHeight: "44px",
                padding: "18px 22px 12px",
                margin: 0,
                background: "#ffffff",
                border: "none",
                boxSizing: "border-box"
              }}
            >
              <span
                className="mfr-sample-tag"
                style={{
                  display: "block",
                  margin: 0,
                  padding: 0,
                  color: "#00652d",
                  fontFamily: "'Work Sans', Arial, sans-serif",
                  fontSize: "12px",

                  fontWeight: 500,
                  lineHeight: 1.5,
                  letterSpacing: 0,
                  textTransform: "none"
                }}
              >
                Sample Portfolio Health Check – an actual portfolio, personal details removed
              </span>
            </header>

            {/* Green diagnosis banner */}
            <div style={{
              background: "#00652d",
              color: "#fff",
              padding: "20px 22px 17px"
            }}>
              <div style={{
                fontFamily: "'Work Sans', Arial, sans-serif",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: ".05em",
                marginBottom: "9px"
              }}>
                WHAT THIS INVESTOR'S HEALTH CHECK FOUND
              </div>

              <h2 style={{
                margin: 0,
                color: "#fff",
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontSize: "20px !important",
                fontWeight: 300,
                lineHeight: 1.2
              }}>
                <p style={{ fontSize: 40, color: "white" }}>

                  Elevated risk of over-diversification, with signs of fragmentation
                </p>
              </h2>
            </div>

            {/* Portfolio summary */}
            <div style={{
              padding: "14px 22px 12px",
              fontSize: "12px",
              lineHeight: 1.7,
              color: "#43574b"
            }}>
              ₹40.78 L across 14 equity and hybrid funds, from a ₹68.90 L statement.
              ₹28.11 L in gold, arbitrage and other non-equity funds was set aside.
            </div>

            {/* Simplified findings */}
            <div className="mfr-findings-list" style={{
              padding: "3px 22px 0"
            }}>

              {/* Finding 1 */}
              <article className="mfr-finding mfr-finding--high" style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(220px, .58fr)",
                gap: "22px",
                padding: "14px 0",
                border: "none",
                background: "transparent"
              }}>
                <div style={{
                  paddingLeft: "14px",
                  borderLeft: "1px solid #8bc900"
                }}>
                  <h3 style={{
                    margin: "0 0 8px",
                    fontFamily: "'Instrument Serif', Georgia, serif",
                    fontSize: "25px",
                    fontWeight: 400,
                    color: "#17432f"
                  }}>
                    1. Elevated risk of over-diversification
                  </h3>

                  <p style={{
                    margin: "0 0 7px",
                    fontSize: "11px",
                    lineHeight: 1.65,
                    color: "#43574b"
                  }}>
                    14 distinct equity and hybrid schemes for ₹40.8 lakh of current value.
                    Most of the money sits in a few of them — the largest 5 hold 85.6% —
                    while several others are too small to make much difference.
                  </p>

                  <p style={{
                    margin: 0,
                    fontSize: "11px",
                    lineHeight: 1.65,
                    color: "#43574b"
                  }}>
                    For ₹40.8 lakh, 14 schemes is clearly more than a focused design needs.
                    Each fund already holds many companies, so additional schemes add
                    repetition more than protection and blur the role of each holding.
                    Companies held by more than one fund are a holdings fact, not money wasted.
                  </p>
                </div>

                <aside style={{
                  padding: "0 0 0 12px",
                  borderLeft: "1px solid #dedfd7"
                }}>
                  <div style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: ".05em",
                    color: "#00652d",
                    marginBottom: "7px"
                  }}>
                    WORTH DECIDING NEXT
                  </div>

                  <p style={{
                    margin: 0,
                    fontSize: "11px",
                    lineHeight: 1.6,
                    color: "#43574b"
                  }}>
                    Review which funds carry a distinct, lasting role and which duplicate
                    one another. A FinEdge Investment Manager can take you through this
                    against your goals.
                  </p>
                </aside>
              </article>

              {/* Finding 2 */}
              <article className="mfr-finding mfr-finding--medium" style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(220px, .58fr)",
                gap: "22px",
                padding: "14px 0",
                border: "none",
                background: "transparent"
              }}>
                <div style={{
                  paddingLeft: "14px",
                  borderLeft: "1px solid #8bc900"
                }}>
                  <h3 style={{
                    margin: "0 0 8px",
                    fontFamily: "'Instrument Serif', Georgia, serif",
                    fontSize: "25px",
                    fontWeight: 400,
                    color: "#17432f"
                  }}>
                    2. Several funds too small to shape results
                  </h3>

                  <p style={{
                    margin: "0 0 7px",
                    fontSize: "11px",
                    lineHeight: 1.65,
                    color: "#43574b"
                  }}>
                    Your eight smallest funds together account for 7.5% of the analysed
                    equity and hybrid portfolio. Individually, they represent between
                    0.2% and 2.4% each.
                  </p>

                  <p style={{
                    margin: 0,
                    fontSize: "11px",
                    lineHeight: 1.65,
                    color: "#43574b"
                  }}>
                    Fragmentation is different from over-diversification: it is about
                    holdings too small to make a difference to the outcome, so they add
                    tracking and decisions without adding much. Only you can say whether
                    each allocation was made for a purpose.
                  </p>
                </div>

                <aside style={{
                  padding: "0 0 0 12px",
                  borderLeft: "1px solid #dedfd7"
                }}>
                  <div style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: ".05em",
                    color: "#00652d",
                    marginBottom: "7px"
                  }}>
                    WORTH DECIDING NEXT
                  </div>

                  <p style={{
                    margin: 0,
                    fontSize: "11px",
                    lineHeight: 1.6,
                    color: "#43574b"
                  }}>
                    For each small holding, decide whether it deserves a meaningful
                    allocation or no place at all.
                  </p>
                </aside>
              </article>

            </div>

            {/* Evidence behind the diagnosis - ORIGINAL SECTION PRESERVED */}
            <div
              className="mfr-details-header"
              style={{
                width: "100%",
                margin: "18px 0 0",
                padding: "0 24px",
                boxSizing: "border-box"
              }}
            >
              <h3 style={{ margin: 0 }}>Evidence behind the diagnosis</h3>
            </div>

            {/* Dropdown 1 - Largest fund allocations */}
            <details
              className="mfr-details"
              style={{
                display: "block",
                width: "100%",
                margin: 0,
                boxSizing: "border-box"
              }}
            >
              <summary style={{
                width: "100%",
                margin: 0,
                paddingLeft: "28px",
                boxSizing: "border-box"
              }}>
                Largest fund allocations
              </summary>

              <div className="mfr-fund-list">
                {[
                  { name: "Franklin India Flexi Cap Fund Regular-Growth", amount: "₹10.52 L", percentage: 25.8 },
                  { name: "DSP Large & Mid Cap Fund Regular-Growth", amount: "₹6.46 L", percentage: 15.8 },
                  { name: "Edelweiss Flexi Cap Fund Regular-Growth", amount: "₹3.18 L", percentage: 7.8 },
                  { name: "Invesco India Mid Cap Fund Regular-Growth", amount: "₹2.81 L", percentage: 6.9 },
                  { name: "DSP Value Fund Regular-Growth", amount: "₹98,447", percentage: 2.4 },
                  { name: "ICICI Prudential Multi Cap Fund-Growth", amount: "₹97,244", percentage: 2.4 },
                  { name: "HDFC Flexi Cap Fund Regular-Growth", amount: "₹29,574", percentage: 0.7 },
                  { name: "Motilal Oswal Large and Midcap Fund Regular-Growth", amount: "₹20,942", percentage: 0.5 },
                  { name: "Edelweiss Mid Cap Fund Regular-Growth", amount: "₹20,267", percentage: 0.5 },
                  { name: "HDFC NIFTY Next 50 Index Fund Regular-Growth", amount: "₹19,290", percentage: 0.5 },
                  { name: "Mirae Asset Healthcare Fund Regular-Growth", amount: "₹10,493", percentage: 0.3 },
                  { name: "Motilal Oswal Nifty Capital Market Index Fund Regular-Growth", amount: "₹10,177", percentage: 0.2 },
                ].map((fund) => (
                  <div className="mfr-fund-item" key={fund.name}>
                    <div className="mfr-fund-item-header">
                      <span className="mfr-fund-item-name">{fund.name}</span>
                      <span className="mfr-fund-item-value">
                        {fund.amount} · {fund.percentage.toFixed(1)}%
                      </span>
                    </div>

                    <div className="mfr-fund-bar">
                      <div
                        className="mfr-fund-bar-fill"
                        style={{ width: `${fund.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <p className="mfr-fund-note">
                Historical amount invested in these funds: ₹39.86 L — not their current value.
              </p>

              <p className="mfr-fund-note">
                Set aside from stock analysis: Axis Greater China Equity FoF Regular-Growth
                (₹5.78 L); DSP Arbitrage Fund Regular-Growth (₹2.81 L); Edelweiss Arbitrage
                Fund Regular-Growth (₹1,775); Edelweiss Gold and Silver ETF FoF Regular-Growth
                (₹7.03 L); Franklin India Arbitrage Fund Regular-Growth (₹1.89 L); Invesco
                India Arbitrage Fund Regular-Growth (₹3.30 L); Motilal Oswal Gold and Silver
                Passive FoF Regular-Growth (₹9,284); SBI Gold Fund-Growth (₹7.19 L).
              </p>
            </details>

            {/* Dropdown 2 - Shared companies and overlapping fund pairs */}
            <details
              className="mfr-details"
              style={{
                display: "block",
                width: "100%",
                margin: 0,
                boxSizing: "border-box"
              }}
            >
              <summary style={{
                width: "100%",
                margin: 0,
                paddingLeft: "28px",
                boxSizing: "border-box"
              }}>
                Shared companies and the most overlapping fund pairs
              </summary>

              <div className="mfr-overlap-layout">

                <div className="mfr-overlap-column">
                  <h4>Largest company exposures across the portfolio</h4>

                  {[
                    { name: "ICICI Bank Ltd.", detail: "via 7 funds", value: "3.8%" },
                    { name: "HDFC Bank Ltd.", detail: "via 5 funds", value: "2.8%" },
                    { name: "Eternal Ltd. (formerly Zomato Ltd.)", detail: "via 7 funds", value: "2.3%" },
                    { name: "Axis Bank Ltd.", detail: "via 6 funds", value: "2.3%" },
                    { name: "Krishna Institute of Medical Sciences Ltd.", detail: "via 4 funds", value: "1.6%" },
                    { name: "Sai Life Sciences Ltd.", detail: "via 3 funds", value: "1.6%" },
                    { name: "Prestige Estates Projects Ltd.", detail: "via 7 funds", value: "1.5%" },
                    { name: "InterGlobe Aviation Ltd.", detail: "via 5 funds", value: "1.4%" },
                    { name: "Max Healthcare Institute Ltd.", detail: "via 7 funds", value: "1.4%" },
                    { name: "State Bank of India", detail: "via 5 funds", value: "1.3%" },
                  ].map((company) => (
                    <div className="mfr-overlap-item" key={company.name}>
                      <span className="mfr-overlap-item-name">
                        {company.name}
                        <span className="mfr-overlap-detail"> · {company.detail}</span>
                      </span>
                      <span className="mfr-overlap-item-value">{company.value}</span>
                    </div>
                  ))}
                </div>

                <div className="mfr-overlap-column">
                  <h4>Most overlapping fund pairs</h4>

                  {[
                    {
                      name: "Franklin India Flexi Cap Fund Regular-Growth & HDFC Flexi Cap Fund Regular-Growth",
                      detail: "26 shared",
                      value: "47.3%",
                    },
                    {
                      name: "DSP Large & Mid Cap Fund Regular-Growth & DSP Value Fund Regular-Growth",
                      detail: "29 shared",
                      value: "47.2%",
                    },
                    {
                      name: "Invesco India Mid Cap Fund Regular-Growth & Invesco India Small Cap Fund Regular-Growth",
                      detail: "32 shared",
                      value: "43.4%",
                    },
                    {
                      name: "Edelweiss Flexi Cap Fund Regular-Growth & Franklin India Flexi Cap Fund Regular-Growth",
                      detail: "28 shared",
                      value: "42.6%",
                    },
                    {
                      name: "Edelweiss Flexi Cap Fund Regular-Growth & Edelweiss Mid Cap Fund Regular-Growth",
                      detail: "57 shared",
                      value: "40.4%",
                    },
                    {
                      name: "DSP Large & Mid Cap Fund Regular-Growth & HDFC Flexi Cap Fund Regular-Growth",
                      detail: "20 shared",
                      value: "39.1%",
                    },
                  ].map((pair) => (
                    <div className="mfr-overlap-item" key={pair.name}>
                      <span className="mfr-overlap-item-name">
                        {pair.name}
                        <span className="mfr-overlap-detail"> · {pair.detail}</span>
                      </span>
                      <span className="mfr-overlap-item-value">{pair.value}</span>
                    </div>
                  ))}
                </div>

              </div>
            </details>

            {/* Original disclaimer preserved */}
            <p
              className="mfr-overlap-footnote"
              style={{
                width: "100%",
                margin: 0,
                padding: "12px 24px 18px",
                boxSizing: "border-box"
              }}
            >
              Disclaimer: The information shown above is for illustrative purposes only
              and represents a sample portfolio analysis.
            </p>

          </section>

        </>
      )}

      {onClose && !isClient && (
        <footer className="mfr-bottom" style={{ marginTop: '28px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="mfr-action-button"
            style={{ background: '#718076', borderColor: '#718076' }}
            onClick={onClose}
          >
            Close Portfolio Review
          </button>
        </footer>
      )}
    </div>
  );
}

function Status({ title, text, spinner, action, tone }) {
  return (
    <div
      className={`phc-card phc-status mfr-status ${tone === 'error' ? 'phc-status--error' : ''
        }`}
      role="status"
      aria-live="polite"
    >
      {spinner && <span className="phc-spinner" aria-hidden="true" />}
      <p className="phc-status__title">{title}</p>
      {text && <p className="phc-muted">{text}</p>}
      {action && (
        <button
          type="button"
          className="phc-btn mfr-action-button mfr-status-button"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
